import { guestMemberApis } from '@kinderlabs-pos/apis/waivers';
import { TelephoneForm } from '@kinderlabs-pos/frameworks/forms';
import { GuestMemberInfoType, KioskType } from '@kinderlabs-pos/shared-data-type';
import { GuestMemberInfoState, KioskElectronStoreState } from '@kinderlabs-pos/state';
import {
	Box,
	CircularProgress,
	DialogContent,
	Dialog,
	DialogContentText,
	Stack,
	styled,
	DialogActions,
	Button,
	Typography,
} from '@mui/material';
import Grid2 from '@mui/material/Unstable_Grid2';
import BackspaceOutlinedIcon from '@mui/icons-material/BackspaceOutlined';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import { getTelephoneNumberWithDash } from '@kinderlabs-pos/shared-util';
import { useQuery } from '@tanstack/react-query';
import codeReading from 'assets/barcode-scan.gif';
import { useFormik } from 'formik';
import { useSetAtom } from 'jotai';
import { useEffect, useState } from 'react';
import * as Yup from 'yup';
import { KioksUserInfoPage } from '.';
import { BarcodeReadHandler } from '../../components/BarcodeReadHandler';
import { KioskNumberBoard } from '../../components/KioskNumberBoard';
import { PrevNextButton } from '../../components/UserInfoPageComponents/PrevNextButton';
import { TitleWithDesc } from '../../components/UserInfoPageComponents/TitleWithDesc';
import { useKioskRouter } from '../../utils/useKioskRouter';
import { UserInfoPageTemplate } from './UserInfoPageTemplate';
import { WithDialog } from '@kinderlabs-pos/ui-atoms';
import { ArrowRightOutlined } from '@ant-design/icons';

type TelephoneCheckProps = {
	telephone?: string;
	goNextStep: () => void;
	goPrevStep: () => void;
	setAlreadyMember: (guestMember: GuestMemberInfoType) => void;
	setTelephone: (telephone: string) => void;
};

export const UserTelephoneCheckPage = ({
	isFnBKiosk,
	signupImageUrl,
	telephoneProps,
	kioskType,
}: {
	isFnBKiosk: boolean;
	signupImageUrl?: string;
	telephoneProps: TelephoneCheckProps;
	kioskType: KioskType | 'ONLINE-TICKETS';
}) => {
	return (
		<>
			{isFnBKiosk && <FnBKioskMappingPage />}
			{!isFnBKiosk && (
				<TicketMappingPage
					props={telephoneProps}
					signupImageUrl={signupImageUrl}
					kioskType={kioskType}
				/>
			)}
		</>
	);
};

const TicketMappingPage = ({
	props,
	signupImageUrl,
	kioskType,
}: {
	props: TelephoneCheckProps;
	signupImageUrl?: string;
	kioskType: KioskType | 'ONLINE-TICKETS';
}) => {
	const { data: KioskElectronStore } = KioskElectronStoreState.useKioskMainPageLocalSettings();
	const { navigate온라인권발권, navigate키오스크보드, navigate퇴장페이지 } = useKioskRouter();

	const { telephone, setAlreadyMember, setTelephone, goNextStep, goPrevStep } = props;
	const [telephoneValue, setTelephoneValue] = useState<string>(telephone ?? '010');
	const { values, setValues, handleSubmit, touched, errors, isValid, isSubmitting } = useFormik({
		initialValues: { telephone: telephone ?? '010' },
		validationSchema: Yup.object({
			telephone: Yup.string()
				.length(11, '전화번호는 11자리 이어야 합니다.')
				.matches(/^[0][1][0][0-9]+$/, '앞자리 휴대폰 번호는 010 입니다.')
				.required('전화번호를 입력해주세요.'),
		}),
		onSubmit: async ({ telephone }) => {
			try {
				const result = await guestMemberApis.getGuestMemberInfo({
					type: 'telephone',
					value: values.telephone,
				});
				setAlreadyMember(result);
			} catch (error) {
				const errorCode = (error as { code: string; message: string }).code;
				const 신규가입자인경우 = errorCode === 'MEMBER-01';
				if (신규가입자인경우) {
					setTelephone(telephone);
					goNextStep();
				}
			}
		},
		validateOnMount: true,
		enableReinitialize: true,
	});

	useEffect(() => {
		setValues({ telephone: telephoneValue });
	}, [telephoneValue, setValues]);

	return (
		<UserInfoPageTemplate>
			<Stack
				sx={{ width: '100%', height: '100%' }}
				spacing={3}>
				{/* 전화번호 입력 */}
				<Stack sx={{ width: '100%', alignItems: 'center', py: 6 }}>
					<TelephoneInputSection
						telephone={telephoneValue}
						setTelephone={setTelephoneValue}
						error={!!(touched.telephone && errors.telephone)}
						errorMessage={touched.telephone && errors.telephone ? errors.telephone : undefined}
					/>
				</Stack>

				{/* <Stack sx={{ width: '100%', alignItems: 'center' }}> */}
				<PrevNextButton
					nextDisabled={!isValid || isSubmitting}
					endIcon={
						isSubmitting && (
							<CircularProgress
								sx={{ color: 'white' }}
								size={25}
							/>
						)
					}
					prevFn={goPrevStep}
					nextFn={handleSubmit}
					자동돌아가기사용={true}
				/>
				{/* </Stack> */}
				{/* {KioskElectronStore?.skipMembership && ( */}
				{true && (
					<WithDialog
						dialog={(open, closeDialog) => (
							<확인Dialog
								open={open}
								closeDialog={closeDialog}
								onSuccess={async () => {
									switch (kioskType) {
										case 'ONLINE-TICKETS':
											navigate온라인권발권();
											break;
										case 'TICKETS':
										case 'FANDB':
											navigate키오스크보드();
											break;
										case 'JUNGSAN':
											navigate퇴장페이지();
											break;
										case 'CUSTOMER_MAPPING':
											break;
									}
								}}
							/>
						)}>
						{(openDialog) => (
							<Button
								variant='text'
								sx={{ color: 'text.secondary' }}
								onClick={openDialog}
								endIcon={<ArrowRightOutlined />}>
								<Stack
									width={480}
									alignItems={'center'}
									justifyContent={'center'}>
									{kioskType === 'TICKETS' && (
										<Typography variant='subtitle2'>
											멤버십 가입하지 않고 입장권 구매하기
										</Typography>
									)}
									{kioskType === 'ONLINE-TICKETS' && (
										<Typography variant='subtitle2'>
											멤버십 가입하지 않고 온라인예매 입장권 받기
										</Typography>
									)}
								</Stack>
							</Button>
						)}
					</WithDialog>
				)}
			</Stack>
		</UserInfoPageTemplate>
	);
};

const BarcodeReaderSection = () => {
	const setFinalUser = useSetAtom(KioksUserInfoPage.finalUserInfo);
	const { navigate키오스크보드 } = useKioskRouter();
	return (
		<Stack sx={{ width: '100%', alignItems: 'center' }}>
			<BarcodeReadHandler
				onEnter={async (value) => {
					try {
						const { data } = useQuery({
							...GuestMemberInfoState.keys.search({ params: { search: value } }),
							keepPreviousData: false,
							refetchOnWindowFocus: true,
						});
						const infoList = data?.content ?? [];
						const userInfo = getUserInfo(infoList);
						if (userInfo) {
							setFinalUser({
								memberId: userInfo.id,
								name: userInfo.name,
								telephone: userInfo.telephone,
							});
						}
					} catch (error) {
						return;
					} finally {
						// mapping 되든 안되는 일단 보드 페이지로 넘어간다.
						navigate키오스크보드();
					}
				}}
			/>
			<BarcodeReading />
		</Stack>
	);
};

const getUserInfo = (list: GuestMemberInfoType[]): GuestMemberInfoType | undefined => {
	if (list.length === 1) return list[0];
	else return undefined;
};

const FnBKioskMappingPage = () => {
	const { values, handleChange, handleBlur, setValues, handleSubmit, touched, errors, isValid } =
		useFormik({
			initialValues: { telephone: '010' },
			onSubmit: async () => {
				try {
					const result = await guestMemberApis.getGuestMemberInfo({
						type: 'telephone',
						value: values.telephone,
					});
					setFinalUserInfo({ memberId: result.id, name: result.name, telephone: result.telephone });
				} catch (error) {
					console.log(error);
				} finally {
					navigate키오스크보드();
				}
			},
		});
	const setFinalUserInfo = useSetAtom(KioksUserInfoPage.finalUserInfo);
	const { navigate키오스크보드 } = useKioskRouter();
	const [pinValues, setPinValues] = useState<string[]>(['0', '1', '0']);

	const handleClickPinValue = (value: number) => {
		if (pinValues.length < 11) setPinValues([...pinValues, `${value}`]);
	};

	const clearPinValues = () => {
		setPinValues([]);
	};

	useEffect(() => {
		setValues((prev) => ({ ...prev, telephone: pinValues.join('') }));
	}, [pinValues, setValues]);

	return (
		<UserInfoPageTemplate>
			<Stack
				sx={{ width: '100%' }}
				spacing={5}>
				<TitleWithDesc title={'주문을 위해 바코드 리더기에 \n입장권을 태깅해주세요.'} />
				<BarcodeReaderSection />
				<TitleWithDesc title={'입장권이 없는 경우\n 발권 시 사용한 핸드폰 번호를 입력해주세요'} />
				<TelephoneForm
					name={'telephone'}
					value={values.telephone}
					onChange={handleChange}
					onBlur={handleBlur}
					error={!!(touched.telephone && errors.telephone)}
					helperText={(touched.telephone && errors.telephone) || 'ㅤ'}
					placeholder={'예)010-1234-5678'}
				/>
				<KioskNumberBoard
					onClickNumber={(value) => {
						if (value === 'clear') {
							clearPinValues();
						} else if (value === 'delete') {
							setPinValues(pinValues.slice(0, -1));
						} else {
							handleClickPinValue(value);
						}
					}}
					customsize='small'
				/>
				<PrevNextButton
					nextDisabled={!isValid}
					nextFn={handleSubmit}
					notUsingPrevBtn
				/>
			</Stack>
		</UserInfoPageTemplate>
	);
};

const BarcodeReading = styled(Box)(({ theme }) => ({
	backgroundImage: `url(${codeReading})`,
	backgroundRepeat: 'no-repeat',
	backgroundSize: 'cover',
	width: '250px',
	height: '250px',
}));

const 확인Dialog = ({
	open,
	closeDialog,
	onSuccess,
}: {
	open: boolean;
	closeDialog: () => void;
	onSuccess: () => void;
}) => {
	return (
		<Dialog
			open={open}
			maxWidth='lg'>
			<DialogContent>
				<DialogContentText>
					<Typography variant='body2'>{`비회원 티켓구매 혹은 비회원 티켓출력 선택 시 주렁주렁 멤버십 혜택을 받지 못 하게 됩니다.`}</Typography>
				</DialogContentText>
			</DialogContent>
			<DialogActions>
				<Stack
					width='100%'
					direction={'row'}
					gap={2}>
					<Button
						sx={{ flex: 1 }}
						variant={'contained'}
						onClick={closeDialog}>
						<Typography variant='body2'>멤버십 가입하기</Typography>
					</Button>
					<Button
						sx={{ flex: 1 }}
						variant={'outlined'}
						onClick={onSuccess}>
						<Typography variant='body2'>비회원 티켓 구매/출력 계속</Typography>
					</Button>
				</Stack>
			</DialogActions>
		</Dialog>
	);
};

const StyledNumberButton = styled(Button)(({ theme }) => ({
	aspectRatio: '1/1',
	borderRadius: 8,
	border: `2px solid ${theme.palette.primary.main}`,
	minHeight: 70,
	fontSize: '1.5rem',
	fontWeight: 600,
	'&:hover': {
		borderWidth: '3px',
		transform: 'scale(1.02)',
		transition: 'all 0.2s ease-in-out',
	},
}));

const TelephoneInputSection = ({
	telephone,
	setTelephone,
	error,
	errorMessage,
}: {
	telephone: string;
	setTelephone: (telephone: string) => void;
	error: boolean;
	errorMessage?: string;
}) => {
	const handleNumberClick = (number: number) => {
		if (telephone.length < 11) {
			setTelephone(telephone + number.toString());
		}
	};

	const handleDelete = () => {
		if (telephone.length > 0) {
			setTelephone(telephone.slice(0, -1));
		}
	};

	const handleClearAll = () => {
		setTelephone('');
	};

	return (
		<Stack
			spacing={3}
			sx={{ width: 500, px: 2 }}>
			<Stack
				direction='row'
				spacing={1}
				sx={{
					p: 3,
					pt: 0,
					// borderRadius: 3,
					// border: '2px solid',
					// borderColor: error ? 'error.main' : 'primary.main',
					// backgroundColor: error ? 'error.light' : 'background.paper',
					minHeight: 80,
					alignItems: 'center',
					justifyContent: 'center',
					// transition: 'all 0.2s ease-in-out',
				}}>
				<Typography
					variant='h3'
					sx={{
						letterSpacing: 2,
						fontWeight: 600,
						color: telephone ? 'text.primary' : 'text.secondary',
					}}>
					{telephone ? getTelephoneNumberWithDash(telephone) : '입력하세요'}
				</Typography>
			</Stack>

			{/* 에러 메시지 */}
			{error && errorMessage && (
				<Typography
					variant='body2'
					color='error'
					sx={{
						textAlign: 'center',
						px: 2,
						py: 1,
						borderRadius: 1,
						backgroundColor: 'error.light',
					}}>
					{errorMessage}
				</Typography>
			)}

			{/* 번호 입력기 */}
			<Grid2
				container
				spacing={2.5}
				sx={{ mt: 1 }}>
				{/* 1-9 숫자 버튼 */}
				{[1, 2, 3, 4, 5, 6, 7, 8, 9].map((num) => (
					<Grid2
						xs={4}
						key={num}>
						<StyledNumberButton
							variant='outlined'
							fullWidth
							onClick={() => handleNumberClick(num)}>
							<Typography variant='h4'>{num}</Typography>
						</StyledNumberButton>
					</Grid2>
				))}

				{/* 지우기 버튼 */}
				<Grid2 xs={4}>
					<StyledNumberButton
						variant='outlined'
						fullWidth
						onClick={handleDelete}>
						<BackspaceOutlinedIcon sx={{ fontSize: 36 }} />
					</StyledNumberButton>
				</Grid2>

				{/* 0 버튼 */}
				<Grid2 xs={4}>
					<StyledNumberButton
						variant='outlined'
						fullWidth
						onClick={() => handleNumberClick(0)}>
						<Typography variant='h4'>0</Typography>
					</StyledNumberButton>
				</Grid2>

				{/* 다지우기 버튼 */}
				<Grid2 xs={4}>
					<StyledNumberButton
						variant='outlined'
						fullWidth
						onClick={handleClearAll}>
						<DeleteOutlinedIcon sx={{ fontSize: 36 }} />
					</StyledNumberButton>
				</Grid2>
			</Grid2>
		</Stack>
	);
};
